/**
 * Client accessors for the account-root billing surface (backend `BillingAccountController`,
 * billing-and-plans §C.3). Every call funnels through `@repo/api-client`'s fetch mutator (bearer token,
 * RFC 9457 `ApiError`). Shapes are declared locally until the OpenAPI-generated models land (`just gen`);
 * field names mirror the controller DTOs exactly. The account root only (R1) may mutate — a manager/waiter
 * gets 403 from the server; the UI additionally gates the controls on `isAccountRoot`.
 */
import { customInstance } from "@repo/api-client";

export type Money = { amountMinor: number; currency: string };

/** The account's billing identity (buyer block on invoices + the Stripe customer). */
export type BillingProfile = {
  legalName: string;
  addressLine: string;
  postalCode: string;
  city: string;
  country: string;
  vatNumber: string;
  version: number;
};

export type Plan = {
  code: string;
  name: string;
  monthlyPrice: Money;
  taxRate: string;
  taxInclusive: boolean;
  maxVenues: number;
  staffSeats: number;
  smsCredits: number;
  mediaQuotaBytes: number;
  includedFlags: string[];
};

export type AddOn = { code: string; name: string; monthlyPrice: Money; flag: string };

export type PlanCatalog = { plans: Plan[]; addOns: AddOn[] };

export type SubscriptionState =
  | "PREVIEW"
  | "TRIALING"
  | "ACTIVE"
  | "PAST_DUE"
  | "SUSPENDED"
  | "CANCELLED";

export type Subscription = {
  venueId: string;
  planCode: string | null;
  addOnCodes: string[];
  state: SubscriptionState;
  currentPeriodStartSec: number | null;
  currentPeriodEndSec: number | null;
  /** A cancel is scheduled at the period end: still ACTIVE with access until currentPeriodEndSec, then CANCELLED. */
  cancelAtPeriodEnd: boolean;
  version: number;
};

const base = "/api/v1/billing";

export const billingApi = {
  plans: (signal?: AbortSignal) =>
    customInstance<PlanCatalog>(`${base}/plans`, { method: "GET", signal }),

  subscription: (venueId: string, signal?: AbortSignal) =>
    customInstance<Subscription | null>(
      `${base}/venues/${encodeURIComponent(venueId)}/subscription`,
      {
        method: "GET",
        signal,
      },
    ),

  changePlan: (venueId: string, planCode: string, addOnCodes: string[]) =>
    customInstance<Subscription>(
      `${base}/venues/${encodeURIComponent(venueId)}/subscription/plan`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planCode, addOnCodes }),
      },
    ),

  /**
   * A Stripe Checkout URL to subscribe the venue — Stripe collects the card + creates the subscription; we
   * mirror it via the webhook. The browser redirects to the returned url.
   */
  checkout: (
    venueId: string,
    planCode: string,
    addOnCodes: string[],
    successUrl: string,
    cancelUrl: string,
  ) =>
    customInstance<{ url: string }>(
      `${base}/venues/${encodeURIComponent(venueId)}/subscription/checkout`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planCode, addOnCodes, successUrl, cancelUrl }),
      },
    ),

  /** Schedule cancel-at-period-end (in-app, no Stripe portal): stays ACTIVE until the period end. */
  cancelSubscription: (venueId: string) =>
    customInstance<Subscription>(
      `${base}/venues/${encodeURIComponent(venueId)}/subscription/cancel`,
      { method: "POST" },
    ),

  /** Undo a scheduled cancel — resume the subscription (Stripe cancel_at_period_end=false). */
  reactivateSubscription: (venueId: string) =>
    customInstance<Subscription>(
      `${base}/venues/${encodeURIComponent(venueId)}/subscription/reactivate`,
      { method: "POST" },
    ),

  /** The account's billing identity (legal name, address, numéro de TVA) for the invoice buyer block. */
  getProfile: (signal?: AbortSignal) =>
    customInstance<BillingProfile>(`${base}/profile`, { method: "GET", signal }),

  /** Upsert the billing identity (CAS on version). Returns the saved profile. */
  putProfile: (profile: BillingProfile) =>
    customInstance<BillingProfile>(`${base}/profile`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(profile),
    }),

  /** A Stripe Billing Portal URL — the hosted surface for the card + invoice history/PDF (Stripe is the record). */
  portalSession: (returnUrl: string) =>
    customInstance<{ url: string }>(`${base}/portal-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ returnUrl }),
    }),
};

/** Format integer minor units as a localized currency amount (ADR-0024 — never a float in transit). */
export function formatMoney(m: Money, locale = "fr-FR"): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency: m.currency }).format(
    m.amountMinor / 100,
  );
}
